import { Breadcrumb } from "@/components/dashboard/breadcrumb";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** @deprecated A page title names a region, so it never takes an eyebrow (eyebrows label data
   *  points only). Accepted-but-ignored so existing callers still type-check; strip as pages are
   *  reworked. Was causing list pages to carry a "Roles" eyebrow the overview didn't (S1). */
  eyebrow?: string;
  title: React.ReactNode;
  description?: string;
  className?: string;
  /** The current page's label for the breadcrumb's last crumb. Only needed where the route can't name the
   *  page itself — i.e. the role detail page passes its role title. Other pages derive it from the route. */
  crumbLabel?: string;
  /** right-aligned slot (actions, filters) */
  children?: React.ReactNode;
}

/** The single page-level heading: a breadcrumb, then a bold Inter h1 + optional description. Used on every
 *  client page so the heading hierarchy is identical across the app. The breadcrumb sits directly above the
 *  title (top-left, where the eye lands) and is present on EVERY page, so the H1 keeps the exact same vertical
 *  position everywhere (the standing constant-header rule) with no empty spacer. The text block spans the full
 *  content width so a long description isn't stranded on one short line with dead space beside it (O2). */
export function PageHeader({ title, description, className, crumbLabel, children }: PageHeaderProps) {
  return (
    <div className={className}>
      <Breadcrumb currentLabel={crumbLabel} />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold leading-tight tracking-tight">{title}</h1>
          {description ? <p className="mt-2 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {children ? <div className="flex shrink-0 items-center gap-2 pt-1">{children}</div> : null}
      </div>
    </div>
  );
}
